import React, { useMemo, useRef, useState } from "react";
import { CardComp, ButtonComp } from "../../../components";
import { formatMoney, convertToTitleCase } from "../../../utils";
import moment from "moment";
import {
  useGetMemberBankTransferPaymentsQuery,
  useUploadMemberBankTransferReceiptMutation,
} from "../../../store/member/member-contribution";
import toast from "react-hot-toast";

const AWAITING = "AWAITING_BANK_CONFIRMATION";

function normalizeRows(listData) {
  if (!listData) return [];
  if (Array.isArray(listData)) return listData;
  if (Array.isArray(listData.data)) return listData.data;
  return [];
}

function inferFlowLabel(row) {
  const pd = row?.payment_data;
  if (pd && typeof pd === "object") {
    if (pd.loan_request_id != null || pd.repayment_id != null) return "Loan repayment";
    if (
      pd.attendance_fine_id != null ||
      pd.fine_id != null ||
      pd.attendance_id != null
    ) {
      return "Attendance fine";
    }
    if (Array.isArray(pd.fields) || pd.contribution_id != null) return "Contribution";
  }
  const ref = String(row?.payment_reference || "").toUpperCase();
  if (ref.includes("LOAN")) return "Loan repayment";
  if (ref.includes("CONTRIBUTION")) return "Contribution";
  if (ref.includes("ATT_FINE") || ref.includes("FINE")) return "Attendance fine";
  const t = String(
    row?.payment_type || row?.metadata?.payment_type || ""
  ).toUpperCase();
  if (t.includes("LOAN")) return "Loan repayment";
  if (t.includes("CONTRIBUTION")) return "Contribution";
  if (t.includes("FINE") || t.includes("ATTENDANCE")) return "Attendance fine";
  return "Payment";
}

function rowMatchesFlowFilter(row, flowFilter) {
  if (!flowFilter) return true;
  const f = flowFilter.toLowerCase();
  const label = inferFlowLabel(row).toLowerCase();
  if (f === "loan") return label.includes("loan");
  if (f === "contribution") return label.includes("contribution");
  if (f === "fine" || f === "attendance")
    return label.includes("attendance") || label.includes("fine");
  return true;
}

function parseJsonMaybe(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (_err) {
    return null;
  }
}

function toArrayFromUnknown(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    const parsed = parseJsonMaybe(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    return value.trim() ? [value.trim()] : [];
  }
  return [];
}

function normalizeReceiptUrls(row) {
  const paymentData = parseJsonMaybe(row?.payment_data);
  const metadata = parseJsonMaybe(row?.metadata);

  const receiptSources = [
    row?.receipt_url,
    row?.receipt_path,
    row?.receipt,
    paymentData?.receipt_url,
    paymentData?.receipt_path,
    metadata?.receipt_url,
    metadata?.receipt_path,
  ].filter(Boolean);

  const receiptPaths = [
    ...toArrayFromUnknown(row?.receipt_paths),
    ...toArrayFromUnknown(paymentData?.receipt_paths),
    ...toArrayFromUnknown(metadata?.receipt_paths),
    ...toArrayFromUnknown(row?.bank_transfer_receipt_paths),
  ].filter(Boolean);

  return [...new Set([...receiptSources, ...receiptPaths])];
}

function isPdfUrl(url) {
  const u = String(url || "").toLowerCase();
  return u.includes(".pdf") || u.includes("application/pdf");
}

/**
 * Shows member-initiated bank transfers that are still awaiting cooperative staff confirmation.
 */
export default function MemberPendingBankTransfersCard({
  title = "Bank transfers awaiting confirmation",
  subtitle = "You chose to pay via transfer. Cooperative staff will confirm after they see the deposit in the society account. You can attach a receipt to help them verify.",
  /** Optional: 'loan' | 'contribution' | 'fine' to only show relevant rows */
  flowFilter = null,
  compact = false,
}) {
  const { data: listData, isLoading: isLoadingList } =
    useGetMemberBankTransferPaymentsQuery({ page: 1, limit: 50 });
  const [uploadReceipt, { isLoading: uploading }] =
    useUploadMemberBankTransferReceiptMutation();
  const [cloudUploading, setCloudUploading] = useState(false);

  const pendingRows = useMemo(() => {
    const rows = normalizeRows(listData);
    return rows.filter((r) => {
      const st = String(r?.status || "").toUpperCase();
      if (st !== AWAITING) return false;
      return rowMatchesFlowFilter(r, flowFilter);
    });
  }, [listData, flowFilter]);

  const fileInputRef = useRef(null);
  const [pendingIdForUpload, setPendingIdForUpload] = useState(null);

  const handleAttachClick = (pendingId) => {
    setPendingIdForUpload(pendingId);
    fileInputRef.current?.click();
  };

  const uploadReceiptToCloudinary = async (file) => {
    const cloudName =
      process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "afrilocales";
    const uploadPreset =
      process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "svmprhvf";
    const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", uploadPreset);

    const res = await fetch(uploadUrl, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      throw new Error("Failed to upload receipt to Cloudinary");
    }
    const data = await res.json();
    return data?.secure_url;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    const id = pendingIdForUpload;
    e.target.value = "";
    setPendingIdForUpload(null);
    if (!file || !id) return;
    try {
      setCloudUploading(true);
      const receiptUrl = await uploadReceiptToCloudinary(file);
      if (!receiptUrl) {
        throw new Error("Receipt URL not returned.");
      }

      await uploadReceipt({ pendingId: id, receiptUrl }).unwrap();
      toast.success("Receipt uploaded.");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Upload failed");
    } finally {
      setCloudUploading(false);
    }
  };

  if (isLoadingList && pendingRows.length === 0) {
    if (compact) return null;
    return (
      <CardComp cardClassName="border-0 mb-4" style={{ borderLeft: "4px solid #ff9500" }}>
        <div className="p-3 text-muted small">Loading bank transfer status…</div>
      </CardComp>
    );
  }

  if (pendingRows.length === 0) return null;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        className="d-none"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={handleFileChange}
      />
      <CardComp
        cardClassName="border-0 mb-4 shadow-sm"
        style={{ borderLeft: "4px solid #ff9500" }}
      >
        <div className="p-4">
          <div className="d-flex align-items-start gap-2 mb-2">
            <span
              className="rounded-pill text-white px-2 py-1 small"
              style={{ backgroundColor: "#ff9500", whiteSpace: "nowrap" }}
            >
              Pending staff
            </span>
          </div>
          <h5 className="fw-bold text-black1 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {title}
          </h5>
          <p className="text-muted small mb-3 mb-md-4">{subtitle}</p>

          <div className="d-flex flex-column gap-3">
            {pendingRows.map((row) => {
              const id = row?.id;
              const refLabel = row?.payment_reference || "—";
              const amt = Number(row?.total_amount ?? 0);
              const created = row?.created_at
                ? moment(row.created_at).format("DD-MMM-YYYY HH:mm")
                : "—";
              const flow = inferFlowLabel(row);
              const receiptUrls = normalizeReceiptUrls(row);
              const hasReceipt = receiptUrls.length > 0;

              return (
                <div
                  key={id || refLabel}
                  className="p-3 rounded"
                  style={{ backgroundColor: "#fafafa", border: "1px solid #e0e0e0" }}
                >
                  <div className="d-flex flex-wrap justify-content-between gap-2 align-items-start">
                    <div>
                      <p className="small text-muted mb-1">Type</p>
                      <p className="fw-semibold text-black1 mb-1">{flow}</p>
                      <p className="small text-muted mb-0">
                        Reference: <span className="text-break">{refLabel}</span>
                      </p>
                      <p className="small text-muted mb-0">Submitted: {created}</p>
                    </div>
                    <div className="text-md-end">
                      <p className="small text-muted mb-1">Amount</p>
                      <p className="fw-bold text-primary1 mb-0" style={{ fontSize: "18px" }}>
                        {formatMoney(Number(amt).toFixed(2))}
                      </p>
                      <span
                        className="rounded-pill text-white px-2 py-1 small d-inline-block mt-1"
                        style={{ backgroundColor: "#ff9500" }}
                      >
                        {convertToTitleCase(String(row?.status || "").replace(/_/g, " "))}
                      </span>
                    </div>
                  </div>
                  {receiptUrls.length > 0 && (
                    <div className="mt-2">
                      <p className="small text-muted mb-1">Receipt{receiptUrls.length > 1 ? "s" : ""}</p>
                      <div className="d-flex flex-wrap gap-2">
                        {receiptUrls.map((url, idx) => (
                          <a
                            key={`${id || refLabel}-receipt-${idx}`}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="small fw-semibold text-secondary2"
                            style={{
                              textDecoration: "underline",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span
                              className="rounded-pill px-2 py-1"
                              style={{
                                border: "1px solid #e0e0e0",
                                background: "#fff",
                                color: "#425466",
                              }}
                            >
                              {isPdfUrl(url) ? "PDF" : "Image"}
                            </span>
                            View
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {id && (
                    <div className="mt-3 pt-2 border-top">
                      <ButtonComp
                        type="button"
                        btnText={
                          cloudUploading || uploading
                            ? "Uploading…"
                            : hasReceipt
                            ? "Update receipt"
                            : "Attach receipt (optional)"
                        }
                        btnClassName="border border-primary1 text-primary1 bg-white px-3 py-2 rounded small"
                        ButtonInlineStyle={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
                        disabled={cloudUploading || uploading}
                        onClick={() => handleAttachClick(id)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardComp>
    </>
  );
}
